"use client";

import { motion } from "motion/react";
import { ArrowRight } from "lucide-react";
import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { Link } from "@/i18n/navigation";
import { CornerFrame } from "../scroll-reveal";

// Hero particles + R3F + drei + three pull in ~226 KB of JS that the
// previous-paint Lighthouse trace measured as 99.98% unused on first paint
// (it only matters once the loader finishes and the canvas mounts). Split
// it out with next/dynamic so the bytes are fetched in the background and
// don't block TTI / TBT on initial load.
const MainSceneBackground = dynamic(
  () => import("@/components/scene-bg/MainSceneBackground"),
  { ssr: false, loading: () => null },
);
import { CountUp } from "../count-up";
import { DelayAsterisk } from "../delay-asterisk";
import { Magnetic } from "../magnetic";
import { WordCascade } from "../text-reveal";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

export function Hero() {
  const t = useTranslations("hero");
  const tConditions = useTranslations("conditions");

  return (
    <section className="relative overflow-hidden">
      {/* Particles logo + local dark backdrop. Sits inside the section so
          it scrolls away with the Hero and doesn't pollute other sections. */}
      <MainSceneBackground />
      {/* Editorial vertical rails — desktop only */}
      <span
        aria-hidden
        className="running-vertical absolute start-4 top-1/2 -translate-y-1/2 hidden lg:block"
      >
        FR · IL · INT · 2026 — Studio digital sur-mesure
      </span>
      <span
        aria-hidden
        className="running-vertical absolute end-4 top-1/2 -translate-y-1/2 hidden lg:block"
      >
        Direction artistique · Code · SEO · 7–14 jours
      </span>

<div className="relative mx-auto flex min-h-[calc(100dvh-4rem)] max-w-[88rem] flex-col items-center justify-center gap-6 px-4 py-16 text-center sm:gap-8 sm:px-6 sm:py-24 lg:px-12 lg:py-32">
        <h1
          data-blend
          className="mx-auto block max-w-[14ch] font-headline text-[clamp(2.5rem,8vw,8rem)] font-light leading-[0.95] tracking-[-0.04em]"
        >
          <WordCascade
            text={t("title")}
            className="block"
            delay={0}
            stagger={0.06}
            duration={0.6}
          />
        </h1>
        <span
          data-blend
          className="font-headline -mt-2 block text-[clamp(2.25rem,7.5vw,7.5rem)] font-light italic leading-[0.95]"
        >
          {t("titleAccent")}
        </span>

        <p
          data-blend
          className="max-w-2xl text-lg leading-relaxed sm:text-xl"
        >
          {t("subtitle")}
        </p>

        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08, delayChildren: 0.55 } },
          }}
          className="mt-2 flex w-full max-w-md flex-col items-stretch gap-3 sm:w-auto sm:max-w-none sm:flex-row sm:items-center"
        >
          <motion.div variants={fadeUp} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full sm:w-auto">
            <Magnetic strength={0.32}>
              <Link
                href={{ pathname: "/", hash: "contact" }}
                className="group relative inline-flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-primary px-6 text-sm font-medium text-primary-foreground transition-all duration-300 hover:bg-primary/90 active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:w-auto"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -z-10 bg-gradient-to-r from-aurora-cyan/0 via-aurora-cyan/30 to-aurora-cyan/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                />
                {t("ctaPrimary")}
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
              </Link>
            </Magnetic>
          </motion.div>
          <motion.div variants={fadeUp} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full sm:w-auto">
            <Link
              href={{ pathname: "/", hash: "services" }}
              className="group inline-flex h-12 w-full items-center justify-center gap-1.5 rounded-full px-5 text-sm font-medium text-foreground transition-colors active:scale-[0.98] sm:w-auto"
            >
              {t("ctaSecondary")}
              <ArrowRight className="size-3.5 opacity-50 transition-all duration-300 group-hover:translate-x-0.5 group-hover:opacity-100 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ y: 18 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 w-full max-w-2xl sm:mt-12"
        >
          <CornerFrame className="grid grid-cols-3 border-t border-border/60 pt-6 sm:pt-8">
            <Metric value={t("metric1Value")} label={t("metric1Label")} asterisk />
            <Metric value={t("metric2Value")} label={t("metric2Label")} divided />
            <Metric value={t("metric3Value")} label={t("metric3Label")} divided />
          </CornerFrame>
          <p data-blend className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] opacity-60 md:text-start">
            <DelayAsterisk />{" "}
            <Link
              href="/conditions-delais"
              className="underline-offset-4 hover:underline"
            >
              {tConditions("asterisk")}
            </Link>
          </p>
        </motion.div>
      </div>

      <div className="absolute inset-x-0 bottom-6 flex justify-center">
        <span data-blend className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-60">
          ↓ scroll
        </span>
      </div>
    </section>
  );
}

function Metric({
  value,
  label,
  divided = false,
  asterisk = false,
}: {
  value: string;
  label: string;
  divided?: boolean;
  asterisk?: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center gap-1 ${
        divided ? "border-s border-border/60" : ""
      }`}
    >
      <span data-blend className="font-headline text-3xl font-light tracking-display sm:text-4xl md:text-5xl">
        <CountUp value={value} />
        {asterisk && <DelayAsterisk />}
      </span>
      <span data-blend className="text-[10px] font-medium uppercase tracking-[0.16em] sm:text-[11px]">
        {label}
      </span>
    </div>
  );
}
